import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/insegnanti`;

const isScadutoPub = (scad) => {
  if (!scad) return false;
  try {
    const d = new Date(scad);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  } catch (e) { return false; }
};

// Colori matrice mensa (coerenti con la Matrice Mensa admin)
const MATBG = { scuola: "#c8e6c9", mensa_scuola: "#b2dfdb", extra: "#b3e5fc", spot: "#ffe0b2" };
const MATFG = { scuola: "#2e7d32", mensa_scuola: "#00695c", extra: "#0277bd", spot: "#e65100" };

export default function InsegnantiPublicPage() {
  const [annoLabel, setAnnoLabel] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // alunno completo
  const [deleghe, setDeleghe] = useState([]);
  const [matrice, setMatrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [anni, setAnni] = useState([]);
  const [annoSel, setAnnoSel] = useState(null); // anno scelto dal filtro (override)

  useEffect(() => {
    axios.get(`${API}/anni`).then(r => setAnni(r.data?.anni || [])).catch(() => setAnni([]));
  }, []);

  // 🆕 Feb 2026: se l'URL contiene ?anno=YYYY (dal QR code), l'usa per interrogare
  // gli endpoint pubblici, altrimenti usa l'anno corrente dal backend.
  const annoParam = (() => {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search);
    const v = p.get("anno");
    return v ? Number(v) : null;
  })();

  const annoEff = annoSel != null ? annoSel : annoParam;

  useEffect(() => {
    if (annoEff) {
      setAnnoLabel(`${annoEff}/${String((annoEff + 1) % 100).padStart(2, "0")}`);
      return;
    }
    axios.get(`${API}/anno-corrente`)
      .then(r => setAnnoLabel(r.data?.label || ""))
      .catch(() => setAnnoLabel(""));
  }, [annoEff]);

  // Ricerca incrementale (debounce 250ms)
  useEffect(() => {
    if (selected) return; // se ho già scelto un alunno, non cerco
    const qn = q.trim();
    if (qn.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true); setErr("");
      try {
        const params = { q: qn };
        if (annoEff) params.anno = annoEff;
        const r = await axios.get(`${API}/alunni`, { params });
        setResults(r.data?.items || []);
      } catch (e) {
        setErr(e.response?.data?.detail || "Errore durante la ricerca");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, selected, annoEff]);

  const pickAlunno = async (al) => {
    setSelected(al);
    setLoading(true); setErr("");
    setMatrice(null);
    try {
      const params = annoEff ? { anno: annoEff } : {};
      const [rd, rm] = await Promise.all([
        axios.get(`${API}/deleghe/${al.cod}`, { params }),
        axios.get(`${API}/matrice/${al.cod}`, { params }).catch(() => null),
      ]);
      setDeleghe(rd.data?.deleghe || []);
      if (rm) setMatrice(rm.data || null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento dati alunno");
      setDeleghe([]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setDeleghe([]);
    setMatrice(null);
    setQ("");
    setResults([]);
    setErr("");
  };

  return (
    <div data-testid="insegnanti-public-page" style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #e3f2fd 0%, #ffffff 50%)",
      fontFamily: "Helvetica, Arial, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        background: "linear-gradient(90deg, #0d47a1, #1565c0)",
        color: "white",
        padding: "20px 16px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 0.5 }}>
            👩‍🏫 Portale Insegnanti — Deleghe Ritiro
          </div>
          <div style={{ fontSize: 12, opacity: 0.95, marginTop: 4 }}>
            Istituto Canossiano · Primaria e Infanzia · Anno scolastico <b>{annoLabel || "—"}</b>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "20px 16px" }}>
        {!selected && (
          <>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 14, lineHeight: 1.5 }}>
              Inserisci <b>cognome o nome dell'alunno</b> per consultare le persone autorizzate al ritiro.
              <br />
              <span style={{ color: "#888", fontSize: 12 }}>
                Min. 2 caratteri. Visibili gli alunni di Primaria e Infanzia.
              </span>
            </div>
            {anni.length > 0 && (
              <div style={{ marginBottom: 12 }} data-testid="insegnanti-anno-filter-wrap">
                <label style={{ fontSize: 12, color: "#555", fontWeight: 700, display: "block", marginBottom: 4 }}>
                  Anno scolastico
                </label>
                <select data-testid="insegnanti-anno-filter" value={annoEff ?? ""}
                  onChange={e => setAnnoSel(e.target.value ? Number(e.target.value) : null)}
                  style={{
                    width: "100%", padding: "10px 12px", fontSize: 15,
                    border: "2px solid #1565c0", borderRadius: 8, outline: "none",
                    boxSizing: "border-box", background: "white", color: "#0d47a1", fontWeight: 700,
                  }}>
                  {annoEff == null && <option value="">Anno corrente</option>}
                  {anni.map(a => (
                    <option key={a} value={a}>{a}/{String((a + 1) % 100).padStart(2, "0")}</option>
                  ))}
                </select>
              </div>
            )}
            <input data-testid="insegnanti-search" autoFocus value={q}
              onChange={e => setQ(e.target.value)} placeholder="Cognome o nome dell'alunno…"
              style={{
                width: "100%", padding: "14px 16px", fontSize: 16,
                border: "2px solid #1565c0", borderRadius: 8, outline: "none",
                boxSizing: "border-box",
              }} />
            {loading && <div style={{ padding: 14, textAlign: "center", color: "#666", fontSize: 13 }}>Ricerca…</div>}
            {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", borderRadius: 4, marginTop: 10, fontSize: 12 }}>{err}</div>}
            {!loading && q.trim().length >= 2 && results.length === 0 && (
              <div style={{ padding: 14, textAlign: "center", color: "#999", fontSize: 13 }}>
                Nessun alunno trovato con questo nome.
              </div>
            )}
            {results.length > 0 && (
              <div data-testid="insegnanti-results" style={{ marginTop: 16, border: "1px solid #bbdefb", borderRadius: 8, overflow: "hidden", background: "white" }}>
                {results.map(al => (
                  <div key={al.cod} data-testid={`insegnanti-pick-${al.cod}`}
                    role="button" tabIndex={0}
                    onClick={() => pickAlunno(al)}
                    onKeyDown={e => { if (e.key === "Enter") pickAlunno(al); }}
                    style={{
                      display: "flex", width: "100%", alignItems: "center", gap: 12,
                      padding: "14px 16px", background: "white",
                      borderBottom: "1px solid #eceff1", cursor: "pointer",
                      textAlign: "left", fontSize: 15,
                    }}
                    onMouseDown={e => e.currentTarget.style.background = "#e3f2fd"}
                    onMouseUp={e => e.currentTarget.style.background = "white"}
                    onMouseLeave={e => e.currentTarget.style.background = "white"}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#0d47a1" }}>{al.cognome} {al.nome}</div>
                      <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>
                        {al.settore === "A" ? "Infanzia" : "Primaria"} · classe <b>{al.classe || "—"}</b>
                      </div>
                      {al.telefono && (
                        <a data-testid={`insegnanti-tel-${al.cod}`} href={`tel:${al.telefono}`}
                          onClick={e => e.stopPropagation()}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 5, marginTop: 5,
                            fontSize: 13, fontWeight: 700, color: "#2e7d32", textDecoration: "none",
                          }}>
                          📞 {al.telefono}
                        </a>
                      )}
                    </div>
                    <span style={{ color: "#1565c0", fontSize: 20 }}>›</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {selected && (
          <>
            <button data-testid="insegnanti-back" onClick={reset}
              style={{
                background: "transparent", color: "#0d47a1", border: "none",
                fontSize: 14, cursor: "pointer", padding: "8px 0", marginBottom: 8,
              }}>
              ← Cerca un altro alunno
            </button>
            <div style={{
              background: "white", borderRadius: 10, border: "1px solid #bbdefb",
              padding: "18px 20px", boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
            }}>
              <div style={{ borderBottom: "2px solid #0d47a1", paddingBottom: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
                  Alunno
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#0d47a1", marginTop: 2 }}>
                  {selected.cognome} {selected.nome}
                </div>
                <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
                  {selected.settore === "A" ? "Infanzia" : "Primaria"} · classe <b>{selected.classe || "—"}</b> · A.S. <b>{annoLabel}</b>
                </div>
              </div>

              {matrice && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, color: "#555", marginBottom: 8, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    🍽 Mensa · settimana tipo{" "}
                    <span style={{ color: "#888", fontWeight: 600, textTransform: "none" }}>
                      ({matrice.tot} {matrice.tot === 1 ? "giorno" : "giorni"})
                    </span>
                  </div>
                  <div data-testid="insegnanti-matrice" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
                    {matrice.giorni.map((g, i) => {
                      const c = matrice.cells[i] || { label: "", kind: "" };
                      const bg = MATBG[c.kind] || "#f5f5f5";
                      const fg = MATFG[c.kind] || "#bbb";
                      return (
                        <div key={g} data-testid={`insegnanti-matrice-day-${i}`} style={{
                          background: bg, borderRadius: 6, padding: "8px 3px", textAlign: "center",
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}>
                          <div style={{ fontSize: 11, color: "#455", fontWeight: 700, textTransform: "uppercase" }}>{g}</div>
                          <div style={{ fontSize: 12, fontWeight: 800, color: fg, marginTop: 4, minHeight: 16, lineHeight: 1.15 }}>
                            {c.label || "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ fontSize: 12, color: "#555", marginBottom: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                Persone autorizzate al ritiro
              </div>

              {loading && <div style={{ padding: 12, color: "#666" }}>Caricamento…</div>}
              {err && <div style={{ padding: 10, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", borderRadius: 4, fontSize: 12 }}>{err}</div>}

              {!loading && !err && deleghe.length === 0 && (
                <div style={{
                  padding: 18, textAlign: "center", color: "#888",
                  background: "#fafafa", border: "1px dashed #ccc", borderRadius: 6, fontSize: 14,
                }}>
                  Nessun delegato registrato per questo alunno.
                </div>
              )}

              {deleghe.map((d, i) => (
                <div key={d.slot ?? i} data-testid={`insegnanti-delegato-${i}`} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px",
                  background: i % 2 === 0 ? "#f1f8e9" : "#e8f5e9",
                  borderLeft: "4px solid #2e7d32",
                  borderRadius: 4,
                  marginBottom: 6,
                }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: "#2e7d32", color: "white",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 13,
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#1b5e20" }}>{d.nome}</div>
                    {d.documento && (
                      <div style={{ fontSize: 12, color: "#33691e", marginTop: 2 }}>
                        📄 {d.documento}
                      </div>
                    )}
                    {d.scadenza_label && (
                      <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2, color: isScadutoPub(d.scadenza) ? "#c62828" : "#558b2f" }}>
                        ⏱ {isScadutoPub(d.scadenza) ? "Documento scaduto il " : "Scadenza documento: "}{d.scadenza_label}
                      </div>
                    )}
                    {d.updated_at_label && (
                      <div style={{ fontSize: 11, color: "#558b2f", marginTop: 2 }}>
                        Inserito il {d.updated_at_label}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <footer style={{ marginTop: 30, textAlign: "center", fontSize: 11, color: "#888" }}>
          canossaHUB · Portale Insegnanti · Accesso pubblico (solo lettura)
        </footer>
      </main>
    </div>
  );
}
